import { Extension } from "@tiptap/core";
import { rowResizing } from "./rowResizing";
import { tableScaling } from "./tableScaling";
import { tableCellAttrPreserve } from "./tableCellAttrPreserve";

export interface RowHeightOptions {
  /**
   * 행 경계 드래그 리사이즈(rowResizing 플러그인) 활성화 여부.
   * false면 속성 등록/렌더링은 유지하되 드래그 UI만 비활성화한다
   * (= 저장된 행 높이는 그대로 표시되지만 사용자가 조절할 수 없음).
   * LumirEditor에서 `tableHandles` prop으로 게이트한다.
   */
  resizable: boolean;
}

/**
 * 표 셀에 `rowHeight`(px) 노드 attr을 추가하는 Tiptap 확장.
 *
 * BlockNote 0.35는 행 높이를 모델링하지 않으므로(`TableContent`에 `columnWidths`만 존재),
 * `verticalAlignment`와 동일한 패턴으로 셀 단위 글로벌 attribute를 등록한다:
 *  - renderHTML이 inline `style="height:Npx"`를 출력 → HTML 복사/내보내기/Excel 붙여넣기에 라운드트립
 *  - parseHTML이 `style.height` 또는 `data-row-height`를 역파싱
 *  - 저장(블록 JSON 주입)은 utils/table-cell-props.ts의 injectTableCellAttrs가 담당
 *  - 로드는 BlockNote blockToNode가 cell.props를 노드 attr로 펼쳐 자동 복원
 *
 * 드래그 리사이즈 UI는 addProseMirrorPlugins에서 rowResizing 플러그인으로 제공하며,
 * resizable 옵션으로 게이트한다.
 */
export const RowHeightExtension = Extension.create<RowHeightOptions>({
  name: "rowHeight",

  addOptions() {
    return { resizable: true };
  },

  addGlobalAttributes() {
    return [
      {
        types: ["tableCell", "tableHeader"],
        attributes: {
          rowHeight: {
            default: null,
            parseHTML: (element) => {
              const fromStyle = parseInt(element.style?.height ?? "", 10);
              if (Number.isFinite(fromStyle) && fromStyle > 0) {
                return fromStyle;
              }
              const fromAttr = parseInt(
                element.getAttribute("data-row-height") ?? "",
                10,
              );
              return Number.isFinite(fromAttr) && fromAttr > 0
                ? fromAttr
                : null;
            },
            renderHTML: (attributes) => {
              const h = attributes.rowHeight;
              if (!h || typeof h !== "number") {
                return {};
              }
              return { style: `height: ${h}px` };
            },
          },
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    // 커스텀 셀 attr 보존은 항상(드래그 비활성이어도 행/열 추가 등으로 유실 방지).
    // 드래그 리사이즈 UI만 resizable로 게이트.
    const plugins = [tableCellAttrPreserve()];
    if (this.options.resizable) {
      plugins.push(rowResizing());
      // 표 전체 종횡비 고정 스케일(우하단 코너 드래그).
      plugins.push(tableScaling());
    }
    return plugins;
  },
});
